import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { compareRoutes, isInRoute } from "util/routeHelpers";
import {
  DownIconStyled,
  DropdownContainer,
  DropdownMenu,
  DropdownNavigationItemContainer,
  DropdownNavigationItemIconTitle,
  DropdownTrigger,
} from "./HeaderDropdownNavigationItem.styled";
import {
  HeaderNavigationItemContainer,
  HeaderNavigationItemIconContainer,
  HeaderNavigationItemIconTitle,
} from "../HeaderNavigation/HeaderNavigationItem/HeaderNavigationItem.styled";
import useDevice from "hooks/useDevice";

const calculateDropdownHeight = (routesLength) => {
  const itemHeight = 20;
  const itemGap = 16;
  const paddingY = 24;

  const visibleItems = routesLength - 1;
  if (visibleItems <= 0) return 0;

  return paddingY + visibleItems * itemHeight + (visibleItems - 1) * itemGap;
};

const DropdownNavigationItem = ({
  routes,
  onClick,
  onToggle,
  isOpen: controlledOpen,
}) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { isDesktop } = useDevice();
  const openState =
    typeof controlledOpen === "boolean" ? controlledOpen : isOpen;
  const dropdownHeight =
    typeof controlledOpen === "boolean" && controlledOpen && !isDesktop
      ? calculateDropdownHeight(routes.length)
      : 0;

  const isInThisRoute = (route) =>
    location?.pathname === "/" && compareRoutes(route.route, "/")
      ? true
      : isInRoute(location.pathname, route.route);

  const toggleDropdown = () => {
    const newState = !openState;
    if (typeof onToggle === "function") onToggle(newState);
    setIsOpen(newState);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    if (typeof onToggle === "function") onToggle(false);
    if (typeof onClick === "function") onClick();
  };

  const isAnyRouteActive = () => {
    return routes.some((route) => isInThisRoute(route));
  };

  useEffect(() => {
    if (!isDesktop) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, isDesktop]);

  useEffect(() => {
    if (typeof controlledOpen === "boolean") {
      setIsOpen(controlledOpen);
    }
  }, [controlledOpen]);

  return (
    <DropdownContainer
      ref={dropdownRef}
      style={{
        marginBottom: `${dropdownHeight}px`,
      }}
    >
      <DropdownTrigger onClick={toggleDropdown}>
        <HeaderNavigationItemContainer as="div" $isActive={isAnyRouteActive()}>
          {routes[0]?.icon && (
            <HeaderNavigationItemIconContainer>
              {routes[0]?.icon}
            </HeaderNavigationItemIconContainer>
          )}
          <HeaderNavigationItemIconTitle
            $isActive={isAnyRouteActive()}
            $highlightTitle={false}
          >
            {routes[0]?.title}
          </HeaderNavigationItemIconTitle>
          <HeaderNavigationItemIconContainer>
            <DownIconStyled $isActive={isAnyRouteActive()} $isOpen={isOpen} />
          </HeaderNavigationItemIconContainer>
        </HeaderNavigationItemContainer>
      </DropdownTrigger>
      <DropdownMenu $open={isOpen}>
        {routes.map((item, index) =>
          index === 0 ? null : (
            <DropdownNavigationItemContainer
              key={index}
              to={item.route}
              $isActive={isInThisRoute(item)}
              onClick={closeDropdown}
            >
              {item?.icon && (
                <HeaderNavigationItemIconContainer>
                  {item?.icon}
                </HeaderNavigationItemIconContainer>
              )}
              <DropdownNavigationItemIconTitle
                $isActive={isInThisRoute(item)}
                $highlightTitle={false}
              >
                {item.title}
              </DropdownNavigationItemIconTitle>
            </DropdownNavigationItemContainer>
          )
        )}
      </DropdownMenu>
    </DropdownContainer>
  );
};

DropdownNavigationItem.propTypes = {
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.node,
      title: PropTypes.string,
      route: PropTypes.string,
    })
  ),
  onClick: PropTypes.func,
  onToggle: PropTypes.func,
  isOpen: PropTypes.bool,
};

export default DropdownNavigationItem;
